/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { ReactElement } from 'react';

import { Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import type { SectionName } from '@coursera/event-pulse-types';

import Markdown from 'bundles/cms/components/Markdown';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import type { DegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';

type CareerInsightCardProps = {
  title?: string | null;
  description?: string | null;
  trackingSection?: SectionName;
};

type ListItemProps = {
  children?: Array<string | ReactElement>;
};

type ListProps = {
  children?: React.ReactElement<ListItemProps>[];
};

const styles = {
  container: (theme: DegreeDescriptionPageTheme) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    padding: 20px var(--cds-spacing-400);
    background-color: var(--cds-color-blue-50);
    border-radius: 8px;

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: ${theme.typography.fontFamily};
      }

      h5 {
        ${theme.mobileFontSizeStyle?.subHeader};
      }

      p {
        ${typography2.bodySecondary}
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-purple-900);
        }
      }
    }

    ${breakpoints.down('sm')} {
      background-color: var(--cds-color-neutral-background-primary);
    }
  `,
  title: () => css`
    width: 100%;
    padding-bottom: 12px;
    margin-bottom: 24px;
    border-bottom: 2px solid var(--cds-color-grey-50);
  `,
  listItem: () => css`
    border-left: 2px solid var(--cds-color-blue-800);
    margin-bottom: 16px;
    padding-left: 12px;
  `,
};

const listRenderer = {
  list: (props: ListProps) => {
    return (
      <>
        {props?.children?.map((child) => (
          <div css={styles.listItem}>
            {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
            <Typography variant="h2" component="h4">
              {child?.props?.children?.[0]}
            </Typography>
            <Typography2 component="p" variant="bodySecondary" color="supportText">
              {child?.props?.children?.[1]}
            </Typography2>
          </div>
        ))}
      </>
    );
  },
};

const CareerInsightCard = ({ title, description, trackingSection }: CareerInsightCardProps) => {
  const theme = useDegreeDescriptionPageTheme();
  const { id } = useDegreeDescriptionPageData();
  const degreeId = id.replace(/^base~/, '');

  return (
    <div css={styles.container(theme)}>
      {title && (
        <>
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography variant="h2semibold" css={[styles.title, theme.mobileFontSizeStyle?.subHeader]} component="h3">
            {title}
          </Typography>
        </>
      )}
      {description && (
        <Markdown
          source={description}
          renderers={listRenderer}
          trackingName="career_section_link"
          buttonTrackingNameV3="degree_info_link"
          trackingSection={trackingSection}
          trackingProductId={degreeId}
        />
      )}
    </div>
  );
};

export default React.memo(CareerInsightCard);
